/**
 * Meta Pixel (browser). Loads only on the published site, never for bots,
 * and only with advertising permission:
 *  - consent regions (EEA, UK, CH): only after the visitor clicks Accept;
 *  - elsewhere: on by default, off once the visitor rejects (opt-out).
 * Region comes from same-origin /cdn-cgi/trace; unknown → treated as consent region.
 * Never sends emails, names, payment references or health/financial details.
 */
import { getConsent, CONSENT_EVENT } from "@/lib/consent";

export const META_PIXEL_ID = "1394650795987989";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};
declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const CONSENT_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL",
  "PL","PT","RO","SK","SI","ES","SE","IS","LI","NO","GB","CH",
]);

let regionPromise: Promise<string> | null = null;
let country = "XX";
let loaded = false;
let revoked = false;

export function visitorCountry(): string {
  return country;
}

function lookupRegion(): Promise<string> {
  if (!regionPromise) {
    regionPromise = (async () => {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 2000);
        const res = await fetch("/cdn-cgi/trace", { signal: ctl.signal });
        clearTimeout(t);
        if (!res.ok) return "XX";
        const m = (await res.text()).match(/^loc=([A-Z0-9]{2})$/m);
        return m?.[1] ?? "XX";
      } catch {
        return "XX";
      }
    })().then((c) => (country = c));
  }
  return regionPromise as Promise<string>;
}

function isProductionHost(): boolean {
  const h = window.location.hostname;
  return !(h === "localhost" || h === "127.0.0.1" || h.includes("id-preview") || h.includes("--"));
}

function isBot(): boolean {
  const ua = navigator.userAgent || "";
  return !!navigator.webdriver || /bot\b|crawl|spider|slurp|headlesschrome|lighthouse|facebookexternalhit|bingpreview/i.test(ua);
}

/** Advertising permission right now (reads consent fresh every call). */
function permitted(): boolean {
  const status = getConsent()?.status;
  if (status === "rejected") return false;
  if (status === "accepted") return true;
  const needsConsent = country === "XX" || country === "T1" || CONSENT_COUNTRIES.has(country);
  return !needsConsent;
}

function call(...args: unknown[]) {
  const f = window.fbq;
  if (!f) return;
  if (typeof f.callMethod === "function") f.callMethod.apply(f, args);
  else f.queue?.push(args);
}

function loadPixel() {
  if (loaded) return;
  loaded = true;
  if (!window.fbq) {
    const n: Fbq = function (...args: unknown[]) {
      if (n.callMethod) n.callMethod.apply(n, args);
      else n.queue!.push(args);
    } as Fbq;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  call("init", META_PIXEL_ID, {}, { agent: "lovable" });
  call("track", "PageView", { country }, { eventID: newEventId() });
  installClickTracking();
}

/** One listener for every button/link click: label + destination, no form values. */
function installClickTracking() {
  document.addEventListener(
    "click",
    (e) => {
      const el = (e.target as Element | null)?.closest?.("button, a, [role='button']") as HTMLElement | null;
      if (!el) return;
      const label = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60);
      const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") || "" : "";
      metaTrack(
        el.tagName === "A" ? "LinkClick" : "ButtonClick",
        { label, href: (href.split("?")[0] ?? "").slice(0, 120), page: window.location.pathname },
        { custom: true },
      );
    },
    { capture: true, passive: true },
  );
}

function sync() {
  if (permitted()) {
    if (!loaded) loadPixel();
    else if (revoked) call("consent", "grant");
    revoked = false;
  } else if (loaded && !revoked) {
    call("consent", "revoke");
    revoked = true;
  }
}

let started = false;
/** Call once from the app shell (useEffect). */
export async function initMetaPixel() {
  if (started || typeof window === "undefined") return;
  started = true;
  if (!isProductionHost() || isBot()) return;
  await lookupRegion();
  sync();
  window.addEventListener(CONSENT_EVENT, sync);
  window.addEventListener("storage", (e) => {
    if (e.key === "statlign_consent_v1") sync();
  });
}

function newEventId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Send a Meta event if allowed. Standard names use "track", others "trackCustom". */
export function metaTrack(
  name: string,
  params: Record<string, unknown> = {},
  opts: { custom?: boolean; eventId?: string } = {},
) {
  if (typeof window === "undefined" || !loaded) return;
  if (!permitted()) return;
  const clean: Record<string, unknown> = { country };
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") clean[k] = v;
  }
  call(opts.custom ? "trackCustom" : "track", name, clean, { eventID: opts.eventId ?? newEventId() });
}

/** Map the app's internal events onto Meta standard/custom events. */
const STANDARD_MAP: Record<string, string> = {
  sign_up: "CompleteRegistration",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
  search: "Search",
  prediction_unlock: "ViewContent",
  contact: "Contact",
  subscribe: "Subscribe",
};
const SKIP = new Set(["page_view", "consent_update", "third_party_loaded", "web_vitals"]);

export function forwardToMeta(name: string, props: Record<string, unknown>) {
  if (SKIP.has(name)) return;
  const std = STANDARD_MAP[name];
  if (std) metaTrack(std, props);
  else metaTrack(name, props, { custom: true });
}

export function metaPageView() {
  if (!loaded || !permitted()) return;
  call("track", "PageView", {}, { eventID: newEventId() });
}
